import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { languages, logoUrl } from "../mock";
import { translations } from "../translations";
import { useLanguage } from "../context/LanguageContext";

// Map display labels to internal language codes
const LANG_CODES = { EN: "en", "繁體": "zh", "简体": "cn" };

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, setLang } = useLanguage();
  const t = translations[lang];

  const navLinks = [
    { label: t.nav.about, path: "/about-us" },
    { label: t.nav.team, path: "/our-team" },
    { label: t.nav.services, path: "/our-services" },
    { label: t.nav.join, path: "/join-us" },
    { label: t.nav.contact, path: "/contact-us" },
  ];

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const Logo = () => (
    <Link to="/" className="flex items-center">
      <img
        src={logoUrl}
        alt={t.brand.legal}
        className="h-[72px] w-auto object-contain"
      />
    </Link>
  );

  return (
    <>
      {/* Utility top bar */}
      <div className="bg-bar text-white/85 text-xs">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 h-11 flex items-center justify-end gap-6">
          <div className="flex items-center gap-3">
            {languages.map((label) => {
              const code = LANG_CODES[label];
              return (
                <button
                  key={label}
                  onClick={() => setLang(code)}
                  className={`transition-colors ${
                    lang === code ? "text-white font-semibold" : "text-white/70 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Logo + nav bar */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 h-[86px] flex items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center">
            {navLinks.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `px-5 py-2.5 mx-0.5 text-[15px] font-serif transition-colors rounded ${
                    isActive ? "bg-sky-accent text-white" : "text-slate-500 hover:text-steel"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="text-steel" size={26} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
        <div
          className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-steel text-white p-8 transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between mb-8">
            <span className="font-serif text-xl font-bold">
              {t.brand.logoMain} {t.brand.logoSub}
            </span>
            <button onClick={() => setOpen(false)} aria-label="Close menu">
              <X size={26} />
            </button>
          </div>
          <nav className="flex flex-col">
            {navLinks.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center justify-between py-4 border-b border-white/10 text-lg font-serif ${
                    isActive ? "text-sky-accent" : "text-white/90"
                  }`
                }
              >
                {item.label}
                <ChevronRight size={18} className="text-white/40" />
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 mt-8">
            {languages.map((label) => {
              const code = LANG_CODES[label];
              return (
                <button
                  key={label}
                  onClick={() => setLang(code)}
                  className={`text-sm px-3 py-1.5 rounded ${
                    lang === code ? "bg-sky-accent text-white" : "bg-white/10 text-white/70"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
